import React, { useEffect, useRef, useState } from 'react';
import { X, Download, Copy, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

const FORMA_PAGO_LABEL = { transferencia: 'Transferencia bancaria', payphone: 'Payphone (tarjeta)' };

export default function CotizacionCompraAsistidaImagenModal({ solicitud, onClose, onEnviada }) {
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [banco, setBanco] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.get('/parametros/').then((res) => {
      const lista = res.data.results || res.data;
      const porClave = Object.fromEntries(lista.map((p) => [p.clave, p.valor]));
      setBanco({
        nombre: porClave.banco_nombre || 'Banco Bolivariano',
        tipo: porClave.banco_tipo_cuenta || 'Cuenta Corriente',
        numero: porClave.banco_numero_cuenta || '',
        ruc: porClave.banco_ruc || '',
        razonSocial: porClave.banco_razon_social || '',
      });
    }).catch(() => setBanco({}));
  }, []);

  useEffect(() => {
    if (!solicitud || !banco) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = 2;
    const W = 480;
    const itemsAltura = (solicitud.items || []).length * 46;
    const H = 620 + itemsAltura;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const YELLOW = '#FEBE00';
    const DARK = '#111111';
    const WHITE = '#FFFFFF';
    const GRAY = '#EFEFEF';
    const GRAY_TEXT = '#666666';
    const BORDER = '#e5e7eb';

    ctx.fillStyle = WHITE;
    ctx.fillRect(0, 0, W, H);

    // Header
    ctx.fillStyle = YELLOW;
    ctx.fillRect(0, 0, W, 80);
    ctx.fillStyle = DARK;
    ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('ECUAFLY', W / 2, 38);
    ctx.font = '11px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = '#333';
    ctx.fillText('Compra Asistida', W / 2, 58);

    ctx.fillStyle = DARK;
    ctx.font = 'bold 17px system-ui, -apple-system, sans-serif';
    ctx.fillText('COTIZACIÓN DE COMPRA', W / 2, 110);

    ctx.strokeStyle = YELLOW;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(40, 120);
    ctx.lineTo(W - 40, 120);
    ctx.stroke();

    let y = 140;
    const leftX = 40;
    const rightX = W - 40;

    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = DARK;
    ctx.textAlign = 'left';
    ctx.fillText('Cliente:', leftX, y);
    ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`${solicitud.nombre} ${solicitud.apellido}`, rightX, y);
    y += 24;

    // Items header
    ctx.fillStyle = GRAY;
    ctx.fillRect(leftX, y - 14, rightX - leftX, 24);
    ctx.font = 'bold 10px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = DARK;
    ctx.textAlign = 'left';
    ctx.fillText('ARTÍCULO', leftX + 6, y);
    ctx.textAlign = 'right';
    ctx.fillText('SUBTOTAL', rightX - 6, y);
    y += 18;

    (solicitud.items || []).forEach((it) => {
      ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = DARK;
      ctx.textAlign = 'left';
      const desc = it.descripcion.length > 38 ? it.descripcion.slice(0, 36) + '…' : it.descripcion;
      ctx.fillText(desc, leftX + 6, y);

      const precio = Number(it.precio_unitario_tienda || 0);
      const subtotal = precio * it.cantidad;
      ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('$' + subtotal.toFixed(2), rightX - 6, y);
      y += 16;

      ctx.font = '10px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = GRAY_TEXT;
      ctx.textAlign = 'left';
      const detalle = `Cant. ${it.cantidad} × $${precio.toFixed(2)}` + (it.variante ? `  ·  ${it.variante}` : '');
      ctx.fillText(detalle, leftX + 6, y);
      y += 14;

      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(leftX, y);
      ctx.lineTo(rightX, y);
      ctx.stroke();
      y += 12;
    });

    y += 4;
    const drawRow = (label, value, opts = {}) => {
      ctx.fillStyle = opts.muted ? GRAY_TEXT : DARK;
      ctx.font = (opts.bold ? 'bold ' : '') + '13px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(label, leftX, y);
      ctx.textAlign = 'right';
      ctx.fillText(value, rightX, y);
      y += 22;
    };

    drawRow('Valor de los productos', '$' + Number(solicitud.valor_productos).toFixed(2));
    drawRow('Comisión compra asistida', '$' + Number(solicitud.comision).toFixed(2));
    y += 6;

    // A pagar ahora — destacado
    ctx.fillStyle = DARK;
    ctx.fillRect(leftX, y - 18, rightX - leftX, 36);
    ctx.fillStyle = WHITE;
    ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('A PAGAR AHORA', leftX + 12, y + 4);
    ctx.fillStyle = YELLOW;
    ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('$' + Number(solicitud.total_pagar_ahora).toFixed(2), rightX - 12, y + 5);
    y += 34;

    ctx.fillStyle = GRAY_TEXT;
    ctx.font = '10px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Esta es la única cantidad que se paga por adelantado.', W / 2, y);
    y += 26;

    // Forma de pago
    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(leftX, y);
    ctx.lineTo(rightX, y);
    ctx.stroke();
    y += 20;

    ctx.fillStyle = DARK;
    ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Forma de pago: ' + (FORMA_PAGO_LABEL[solicitud.forma_pago_preferida] || ''), leftX, y);
    y += 20;

    if (solicitud.forma_pago_preferida === 'transferencia') {
      ctx.font = '11px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#374151';
      const lineas = [
        banco.nombre,
        banco.tipo + (banco.numero ? ' No. ' + banco.numero : ''),
        'RUC: ' + banco.ruc,
        'Razón Social: ' + banco.razonSocial,
      ];
      lineas.forEach((l) => { ctx.fillText(l, leftX, y); y += 15; });
    } else {
      ctx.font = '11px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#374151';
      ctx.fillText('Te enviamos el link de pago de Payphone por separado.', leftX, y);
      y += 15;
      ctx.fillText('Incluye el recargo del 5.75% de la plataforma.', leftX, y);
      y += 15;
    }
    y += 14;

    // Estimado al llegar — SOLO informativo, un único monto (envío + aduana).
    // A propósito NO se suma con "a pagar ahora" acá: mostrar un "total"
    // combinado confundía al cliente, parecía que le faltaba pagar eso.
    ctx.fillStyle = GRAY;
    ctx.fillRect(leftX, y, rightX - leftX, 54);
    ctx.fillStyle = DARK;
    ctx.font = 'bold 11px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Al llegar el paquete a Ecuador (informativo)', leftX + 10, y + 18);
    ctx.font = '10px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = GRAY_TEXT;
    ctx.fillText('Se factura por separado, no incluido en el pago de hoy', leftX + 10, y + 32);
    ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = DARK;
    ctx.textAlign = 'right';
    ctx.fillText('Envío + aduana (est.): $' + Number(solicitud.envio_estimado).toFixed(2), rightX - 10, y + 42);
    y += 74;

    // Footer
    const footerY = Math.max(y, H - 50);
    ctx.fillStyle = DARK;
    ctx.fillRect(0, footerY, W, H - footerY);
    ctx.fillStyle = YELLOW;
    ctx.font = 'bold 12px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('ECUAFLY', W / 2, footerY + 20);
    ctx.fillStyle = '#9ca3af';
    ctx.font = '9px system-ui, -apple-system, sans-serif';
    ctx.fillText('Miami · Shenzhen · Quito  |  info@ecuafly.com', W / 2, footerY + 36);

    const finalH = footerY + 50;
    if (finalH !== H) {
      canvas.height = finalH * dpr;
      canvas.style.height = finalH + 'px';
    }

    setImgUrl(canvas.toDataURL('image/png'));
  }, [solicitud, banco]);

  const descargar = () => {
    if (!imgUrl) return;
    const link = document.createElement('a');
    link.download = `cotizacion_compra_asistida_${solicitud.id}.png`;
    link.href = imgUrl;
    link.click();
    toast.success('Imagen descargada.');
  };

  const copiar = async () => {
    if (!canvasRef.current) return;
    try {
      const blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast.success('Imagen copiada al portapapeles.');
    } catch {
      toast.error('No se pudo copiar. Descarga la imagen en su lugar.');
    }
  };

  const enviarCorreo = async () => {
    setEnviando(true);
    try {
      const res = await api.post(`/compra-asistida/${solicitud.id}/enviar_cotizacion_correo/`);
      toast.success(`Correo enviado a ${solicitud.email}.`);
      if (onEnviada) onEnviada(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo enviar el correo.');
    } finally {
      setEnviando(false);
    }
  };

  if (!solicitud) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Cotización para WhatsApp / Correo</h3>
          <button className="btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
          {!banco ? (
            <p className="text-secondary">Cargando...</p>
          ) : (
            <>
              <canvas ref={canvasRef} style={{ maxWidth: '100%', borderRadius: 'var(--radius-md)', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', justifyContent: 'center', marginTop: 'var(--spacing-4)' }}>
                <button className="btn-primary" onClick={descargar}>
                  <Download size={16} /> Descargar imagen
                </button>
                <button className="btn-secondary" onClick={copiar}>
                  <Copy size={16} /> Copiar al portapapeles
                </button>
              </div>
              <p className="text-xs text-secondary" style={{ marginTop: 'var(--spacing-3)' }}>
                Pega la imagen en WhatsApp, o envía la cotización por correo con el botón de abajo.
              </p>
              <button className="btn-primary" onClick={enviarCorreo} disabled={enviando} style={{ marginTop: 'var(--spacing-2)' }}>
                <Mail size={16} /> {enviando ? 'Enviando...' : `Enviar por correo a ${solicitud.email}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
